import React, { useMemo, useState } from 'react';
import { DillModal } from '../../../general/Dill/DillModal';
// import { twMerge } from 'tailwind-merge';
import { DillButton } from '../../../general/Dill';
import './index.css';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  CreditSafeReport,
  ExperianReport,
  ManualCreditReport,
  NACMReport,
  CreditApplication,
  Supplier,
} from '@dill/dill-shared/dist/src';
import ScorecardCard from './ScorecardCard';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import { ArrowsCounterClockwise, DownloadSimple, Eye, Trash, UploadSimple } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  deleteManualCreditReport,
  getCreditSafeReport,
  getExperianPremierProfile,
  getNACMReport,
} from '../../../../redux/services/creditReportService';
import { downloadPdfFromBuffer, openDillToast } from '../../../../utils/helpers';
import { getSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import CreditSafeScoreCard from './CreditSafeScoreCard';
import ExperianScoreCard from './ExperianScoreCard';
import NACMScoreCard from './NACMScoreCard';
import UploadManualCreditReportModal from './UploadManualCreditReportModal';
import { getBureauScores } from './creditApplicationSummaryUtils';

type PulledReport =
  | (NACMReport & { source: string })
  | (CreditSafeReport & { source: string })
  | (ManualCreditReport & { source: string })
  | (ExperianReport & { source: string });

const CreditReport = ({
  onSeeCreditReport,
  pulledCreditReports,
  onPreviewReport,
  creditApplicationId,
  internalFormSubmissionId,
  onReportsChanged,
  fieldValues,
  supplier,
  creditApplication,
}: {
  onSeeCreditReport: () => void;
  pulledCreditReports?: PulledReport[] | null;
  onPreviewReport: ({
    id,
    html,
    source,
    bufferData,
  }: {
    id: string;
    html: string;
    source: string;
    bufferData?: { data: number[] } | null;
  }) => void;
  creditApplicationId: string;
  // On an internal form's review page: reports are kept on that submission, and
  // onReportsChanged reloads them instead of refetching a credit application.
  internalFormSubmissionId?: string;
  onReportsChanged?: () => void;
  // The internal form's answers, to prefill a manual report upload.
  fieldValues?: CreditApplication['fieldValues'];
  supplier?: Supplier | null;
  creditApplication?: CreditApplication | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState('');
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  const handleDeleteManualReport = async (reportId: string) => {
    if (!currentSupplier?.id) return;
    setDeletingReportId(reportId);
    const result = await dispatch(
      deleteManualCreditReport({
        supplierId: currentSupplier.id,
        creditApplicationId,
        internalFormSubmissionId,
        reportId,
      })
    );
    if (result?.type.includes('fulfilled')) {
      if (onReportsChanged) onReportsChanged();
      else
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier.id,
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
    } else {
      const payload: any = result?.payload;
      openDillToast({
        message: typeof payload === 'string' ? payload : 'Failed to delete report.',
        type: 'ERROR',
      });
    }
    setDeletingReportId('');
  };

  const getTime = (item: any) =>
    item.updatedAt
      ? new Date(item.updatedAt).getTime()
      : item.createdAt
      ? new Date(item.createdAt).getTime()
      : 0;

  const report = useMemo(() => {
    if (!pulledCreditReports) return null;
    const normalizedReports = pulledCreditReports
      .map((r) => ({
        ...r,
        unifiedId:
          'nacmId' in r
            ? (r as any).nacmId
            : 'creditSafeId' in r
            ? (r as any).creditSafeId
            : 'experianBin' in r && !('manualId' in r)
            ? (r as any).experianBin
            : (r as any).manualId,
      }))
      .sort((a, b) => getTime(b) - getTime(a));

    return normalizedReports;
  }, [pulledCreditReports]);

  const [loading, setLoading] = useState<string>('');

  const bureauScores = useMemo(() => getBureauScores(pulledCreditReports), [pulledCreditReports]);
  const creditSafeReport = bureauScores.creditSafe;
  const experianReport = bureauScores.experian;
  const nacmReport = bureauScores.nacm;

  const columns = useMemo(() => {
    return [
      { id: 'name', name: 'Name', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'address', name: 'Address', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'type', name: 'Type', width: 90, cellType: 'VIEW_TEXT' },
      { id: 'updateAt', name: 'Refreshed At', width: 80, cellType: 'VIEW_TEXT' },
      { id: 'source', name: 'Source', width: 80, cellType: 'VIEW_TEXT' },
      { id: 'action', name: 'Action', width: 70, cellType: 'VIEW_TEXT' },
    ];
  }, [report]);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  const handleRefresh = async (
    id: string,
    name: string,
    type: string,
    source: string,
    reportData?: any
  ) => {
    setLoading(id);
    let result = null;
    if (source === 'NACM') {
      result = await dispatch(
        getNACMReport({
          supplierId: currentSupplier?.id ?? '',
          companyNACMID: id,
          name,
          type: type,
          creditApplicationId,
          internalFormSubmissionId,
        })
      );
    } else if (source === 'Experian') {
      result = await dispatch(
        getExperianPremierProfile({
          supplierId: currentSupplier?.id ?? '',
          experianBin: id,
          name,
          type,
          creditApplicationId,
          internalFormSubmissionId,
          street: reportData?.address1,
          city: reportData?.city,
          state: reportData?.state,
          zip: reportData?.zipCode,
        })
      );
    } else {
      result = await dispatch(
        getCreditSafeReport({
          supplierId: currentSupplier?.id ?? '',
          connectId: id,
          name,
          type: type,
          creditApplicationId,
          internalFormSubmissionId,
        })
      );
    }
    if (result?.type.includes('fulfilled')) {
      const payload: any = result?.payload;
      const payloadData = payload?.data;
      let html = payloadData;
      if (source === 'CreditSafe') {
        html = payloadData.htmlData as string;
      }

      onPreviewReport({ html: html, id, source });
      if (onReportsChanged) onReportsChanged();
      else
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
    } else {
      const payload: any = result?.payload;
      openDillToast({
        message: typeof payload === 'string' ? payload : 'Failed to get credit report.',
        type: 'ERROR',
      });
    }
    setLoading('');
  };

  return (
    <div className="flex w-full flex-col mt-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-1 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-bold text-gray-800 tracking-wide">Credit Score & Reports</h2>
        <div className="flex flex-row gap-2">
          <DillButton
            label="Upload Report"
            variant="secondary"
            height="35px"
            icon={<UploadSimple size={15} weight="bold" />}
            onClick={() => setUploadModalOpen(true)}
          />
          {((supplier?.creditReportSettings?.nacmCreditReportToken &&
            supplier?.creditReportSettings?.nacmCreditReportUrl) ||
            (supplier?.creditReportSettings?.experianUsername &&
              supplier?.creditReportSettings?.experianClientId) ||
            (supplier?.creditReportSettings?.creditSafeUsername &&
              supplier?.creditReportSettings?.creditSafePassword)) && (
            <DillButton
              label="Access Credit Report Dashboard"
              onClick={onSeeCreditReport}
              height="35px"
            />
          )}
        </div>
      </div>
      <div className="pt-4 px-4 flex flex-col w-full">
        <div>
          {report && report.length > 0 && (
            <div className="border rounded-lg overflow-hidden mb-4 my-4">
              <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-1 ">
                {columns.map((column) => {
                  return (
                    <div
                      key={column.id}
                      style={{ flex: column.width / totalWidth }}
                      className={twMerge('flex py-3 mx-0.5')}>
                      {column.id !== 'actions' ? (
                        <div
                          className={`flex align-center ${
                            column.id === 'alreadyOn' ? 'justify-center w-full' : ''
                          }`}>
                          <p className="text-xs TEXT_SECONDARY-CLR mr-2 uppercase">{column.name}</p>
                        </div>
                      ) : (
                        <p className="text-xs TEXT_SECONDARY-CLR uppercase">{column.name}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="max-h-[500px] overflow-scroll">
                  {report.map((pulledCreditReport, index) => {
                    const id = pulledCreditReport.unifiedId;
                    return (
                      <div
                        key={index + 'oo'}
                        className="w-full flex items-center  px-4 justify-between border-b border-b-gray-200">
                        {columns.map((column, i) => {
                          return (
                            <div
                              key={column.id}
                              style={{ flex: column.width / totalWidth }}
                              className={`flex py-3 mx-0.5 ${
                                columns.length - 1 !== i ? 'overflow-hidden' : ''
                              }`}>
                              {column.id === 'updateAt' && (
                                <p className="capitalize w-full text-xs">
                                  {pulledCreditReport?.updatedAt
                                    ? moment(pulledCreditReport?.updatedAt).format('MM/DD/YYYY LT')
                                    : ''}
                                </p>
                              )}
                              {column.id === 'name' && (
                                <p className="capitalize w-full text-xs">
                                  {pulledCreditReport?.name ?? ''}
                                </p>
                              )}
                              {column.id === 'address' && (
                                <div className="text-xs">{`${
                                  pulledCreditReport?.address1
                                    ? `${pulledCreditReport?.address1}, `
                                    : ''
                                } ${
                                  pulledCreditReport?.address2
                                    ? `${pulledCreditReport?.address2}, `
                                    : ''
                                } ${
                                  pulledCreditReport?.city ? `${pulledCreditReport?.city}, ` : ''
                                } ${pulledCreditReport?.state ?? ''} ${
                                  pulledCreditReport?.zipCode ?? ''
                                }`}</div>
                              )}
                              {column.id === 'type' && (
                                <div className="capitalize text-xs">
                                  {pulledCreditReport?.type ?? ''}
                                </div>
                              )}
                              {column.id === 'source' && (
                                <div className="capitalize text-xs">
                                  {pulledCreditReport?.source ?? ''}
                                </div>
                              )}
                              {column.id === 'action' && (
                                <div className="flex w-full gap-1">
                                  {/* Manual report: Preview (opens fileUrl) + Delete */}
                                  {pulledCreditReport.source === 'Manual' && (
                                    <>
                                      <div
                                        data-tooltip-id={`preview-manual-report-${pulledCreditReport.id}`}>
                                        <DillButton
                                          variant="icon-default"
                                          height="30px"
                                          width="30px"
                                          icon={<Eye size={17} />}
                                          onClick={() =>
                                            setPreviewPdfUrl(
                                              (pulledCreditReport as ManualCreditReport).fileUrl
                                            )
                                          }
                                          isDisabled={deletingReportId !== ''}
                                        />
                                      </div>
                                      <ReactTooltip
                                        id={`preview-manual-report-${pulledCreditReport.id}`}
                                        place="bottom"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '90px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span>Preview</span>
                                      </ReactTooltip>
                                      <div
                                        data-tooltip-id={`delete-manual-report-${pulledCreditReport.id}`}>
                                        <DillButton
                                          variant="icon-red"
                                          height="30px"
                                          width="30px"
                                          icon={<Trash size={17} />}
                                          onClick={() =>
                                            handleDeleteManualReport(pulledCreditReport.id)
                                          }
                                          isLoading={deletingReportId === pulledCreditReport.id}
                                          isDisabled={deletingReportId !== ''}
                                        />
                                      </div>
                                      <ReactTooltip
                                        id={`delete-manual-report-${pulledCreditReport.id}`}
                                        place="bottom"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '80px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span>Delete</span>
                                      </ReactTooltip>
                                    </>
                                  )}
                                  {/* NACM / CreditSafe: Preview + Download + Refresh */}
                                  {pulledCreditReport.source !== 'Manual' && (
                                    <>
                                      {(pulledCreditReport as any)?.htmlData && (
                                        <div
                                          data-tooltip-id={`preview-nacm-report-${pulledCreditReport.id}`}>
                                          <DillButton
                                            variant="icon-default"
                                            height="30px"
                                            width="30px"
                                            icon={<Eye size={17} />}
                                            onClick={() => {
                                              onPreviewReport({
                                                html: (pulledCreditReport as any)?.htmlData ?? '',
                                                id,
                                                source: pulledCreditReport?.source ?? '',
                                                bufferData: (pulledCreditReport as any)?.bufferData,
                                              });
                                            }}
                                            isDisabled={loading !== ''}
                                          />
                                        </div>
                                      )}
                                      {(pulledCreditReport as any)?.bufferData && (
                                        <div
                                          data-tooltip-id={`download-nacm-report-${pulledCreditReport.id}`}>
                                          <DillButton
                                            variant="icon-default"
                                            height="30px"
                                            width="30px"
                                            icon={<DownloadSimple size={17} />}
                                            onClick={() => {
                                              downloadPdfFromBuffer(
                                                (pulledCreditReport as any)?.bufferData,
                                                `${
                                                  pulledCreditReport?.source ?? ''
                                                }-report-${id}-${new Date().getTime()}.pdf`
                                              );
                                            }}
                                            isDisabled={loading !== ''}
                                          />
                                        </div>
                                      )}
                                      <div
                                        data-tooltip-id={`refresh-nacm-report-${pulledCreditReport.id}`}>
                                        <DillButton
                                          variant="icon-default"
                                          height="30px"
                                          width="30px"
                                          icon={
                                            <ArrowsCounterClockwise
                                              color={COLORS.GREY_500}
                                              size={17}
                                            />
                                          }
                                          onClick={() => {
                                            handleRefresh(
                                              pulledCreditReport.unifiedId ?? '',
                                              pulledCreditReport.originalName,
                                              pulledCreditReport.type,
                                              pulledCreditReport.source,
                                              pulledCreditReport
                                            );
                                          }}
                                          isLoading={loading === id}
                                          isDisabled={loading !== ''}
                                        />
                                      </div>
                                      <ReactTooltip
                                        id={`refresh-nacm-report-${pulledCreditReport.id}`}
                                        place="bottom"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '90px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span>Refresh</span>
                                      </ReactTooltip>
                                      <ReactTooltip
                                        id={`download-nacm-report-${pulledCreditReport.id}`}
                                        place="bottom"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '100px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span>Download</span>
                                      </ReactTooltip>
                                      <ReactTooltip
                                        id={`preview-nacm-report-${pulledCreditReport.id}`}
                                        place="bottom"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '90px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span>Preview</span>
                                      </ReactTooltip>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Built from a credit application's own data (trade references, Plaid, ...). */}
        {!internalFormSubmissionId && (
          <ScorecardCard supplier={supplier} creditApplication={creditApplication} />
        )}

        {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
          <CreditSafeScoreCard
            score={creditSafeReport?.score}
            creditLimit={creditSafeReport?.creditLimit}
          />
        )}

        {(process.env.REACT_APP_ENVIRONMENT !== 'production' || nacmReport) && (
          <NACMScoreCard score={nacmReport?.score} />
        )}

        {(process.env.REACT_APP_ENVIRONMENT !== 'production' || experianReport) && (
          <ExperianScoreCard
            intelliscore={experianReport?.intelliscore}
            financialScoreRisk={experianReport?.financialScoreRisk}
            creditLimit={experianReport?.creditLimit}
            fsrScoreTrends={experianReport?.fsrScoreTrends}
            commercialScoreTrends={experianReport?.commercialScoreTrends}
            modelCode={supplier?.creditReportSettings?.experianModelCode}
          />
        )}
      </div>
      <UploadManualCreditReportModal
        open={uploadModalOpen}
        handleClose={() => setUploadModalOpen(false)}
        creditApplicationId={creditApplicationId}
        creditApplication={creditApplication}
        internalFormSubmissionId={internalFormSubmissionId}
        onReportsChanged={onReportsChanged}
        fieldValues={fieldValues}
      />

      {/* PDF Preview Modal */}
      <DillModal
        open={!!previewPdfUrl}
        onClose={() => setPreviewPdfUrl(null)}
        title="Credit Report Preview"
        maxWidth="90vw"
        contentClassName="flex-1 min-h-0 p-0">
        <iframe
          src={previewPdfUrl ?? ''}
          className="w-full"
          style={{ height: '80vh' }}
          title="Credit Report Preview"
        />
      </DillModal>
    </div>
  );
};

export default CreditReport;
